import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Search, Check } from 'lucide-react';
import { formatCurrency, canModifyRecord, isAdminUser, approvalBadge } from '../utils/helpers';

const empty = { name:'', category_id:'', unit:'PCS', hsn_code:'', tax_rate:0, purchase_price:0, selling_price:0, stock_qty:0, min_stock:0, description:'', status:1 };

export default function Products() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState('');
  const [filterApproval, setFilterApproval] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const admin = isAdminUser();

  const load = () => api.get('/products').then(r => setList(r.data.data || []));
  useEffect(() => {
    load();
    api.get('/categories').then(r => setCats(r.data.data || []));
  }, []);

  const save = async () => {
    if (!form.name) return toast.error('Name required');
    try {
      const r = editId ? await api.put('/products/' + editId, form) : await api.post('/products', form);
      toast.success(r.data.message || 'Saved');
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete product?')) return;
    try { await api.delete('/products/' + id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this product?')) return;
    try { await api.post('/products/' + id); toast.success('Approved'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const edit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); };
  const filtered = list.filter(p => {
    if (filterApproval !== 'all' && p.approval_status !== filterApproval) return false;
    return !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.product_code||'').toLowerCase().includes(q.toLowerCase());
  });

  const pendingCount = list.filter(p => p.approval_status === 'pending').length;

  return (
    <div className="card">
      <div className="card-header">
        <h3>Products ({filtered.length}){pendingCount > 0 && <span className="badge badge-orange" style={{marginLeft:8,fontSize:11}}>{pendingCount} pending</span>}</h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}><Plus size={14}/> Add Product</button>
      </div>
      <div className="search-bar" style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <Search size={16} style={{color:'#94a3b8'}}/>
        <input placeholder="Search product..." value={q} onChange={e=>setQ(e.target.value)} style={{flex:1,minWidth:200}}/>
        <select value={filterApproval} onChange={e=>setFilterApproval(e.target.value)} style={{maxWidth:160}}>
          <option value="all">All Status</option>
          <option value="pending">Pending Only</option>
          <option value="approved">Approved Only</option>
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Unit</th><th className="td-num">Buy</th><th className="td-num">Sell</th><th className="td-num">Stock</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length ? filtered.map(p => {
              const badge = approvalBadge(p.approval_status);
              const canMod = canModifyRecord(p);
              const isPending = p.approval_status === 'pending';
              return (
                <tr key={p.id} style={isPending ? {background:'#fffbeb'} : {}}>
                  <td><strong>{p.product_code}</strong></td>
                  <td>{p.name}</td>
                  <td>{p.category_name || '-'}</td>
                  <td>{p.unit}</td>
                  <td className="td-num">{formatCurrency(p.purchase_price)}</td>
                  <td className="td-num">{formatCurrency(p.selling_price)}</td>
                  <td className={'td-num ' + (parseFloat(p.stock_qty) <= parseFloat(p.min_stock) ? 'text-red' : 'text-green')}>{p.stock_qty}</td>
                  <td>
                    <span className={badge.cls}>{badge.label}</span>
                    {p.created_by_name && <small style={{display:'block',color:'#94a3b8',fontSize:10,marginTop:2}}>by {p.created_by_name}</small>}
                  </td>
                  <td>
                    {admin && isPending && <button className="btn btn-sm btn-green" onClick={()=>approve(p.id)} title="Approve"><Check size={12}/></button>}
                    {' '}
                    {canMod && <button className="btn btn-sm btn-gray" onClick={()=>edit(p)} title="Edit"><Edit2 size={12}/></button>}
                    {' '}
                    {canMod && <button className="btn btn-sm btn-red" onClick={()=>del(p.id)} title="Delete"><Trash2 size={12}/></button>}
                    {!canMod && !isPending && <span style={{color:'#94a3b8',fontSize:11}}>Locked</span>}
                  </td>
                </tr>
              );
            }) : <tr><td colSpan="9" className="empty">No products</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title={editId ? 'Edit Product' : 'Add Product'} size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>{editId ? 'Update' : 'Create'}</button>
        </>}>
        {!editId && !admin && (
          <div style={{background:'#fef3c7',border:'1px solid #fcd34d',padding:'10px 12px',borderRadius:8,marginBottom:14,fontSize:13,color:'#92400e'}}>
            Your entry will be saved as <strong>pending</strong> until an admin approves it.
          </div>
        )}
        <div className="form-grid">
          <div className="form-group"><label>Product Name *</label><input value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
          <div className="form-group"><label>Category</label>
            <select value={form.category_id||''} onChange={e=>setForm({...form, category_id:e.target.value})}>
              <option value="">-- Select --</option>
              {cats.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Unit</label>
            <select value={form.unit} onChange={e=>setForm({...form, unit:e.target.value})}>
              <option>PCS</option><option>KG</option><option>LTR</option><option>MTR</option><option>BOX</option><option>TON</option><option>BAG</option><option>DOZ</option>
            </select>
          </div>
          <div className="form-group"><label>HSN Code</label><input value={form.hsn_code} onChange={e=>setForm({...form, hsn_code:e.target.value})}/></div>
          <div className="form-group"><label>Tax Rate %</label><input type="number" value={form.tax_rate} onChange={e=>setForm({...form, tax_rate:e.target.value})}/></div>
          <div className="form-group"><label>Purchase Price</label><input type="number" value={form.purchase_price} onChange={e=>setForm({...form, purchase_price:e.target.value})}/></div>
          <div className="form-group"><label>Selling Price</label><input type="number" value={form.selling_price} onChange={e=>setForm({...form, selling_price:e.target.value})}/></div>
          <div className="form-group"><label>Stock Qty</label><input type="number" value={form.stock_qty} onChange={e=>setForm({...form, stock_qty:e.target.value})}/></div>
          <div className="form-group"><label>Min Stock Level</label><input type="number" value={form.min_stock} onChange={e=>setForm({...form, min_stock:e.target.value})}/></div>
          <div className="form-group full-width"><label>Description</label><textarea value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
